import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phoneNumber = "919840193160";
    const message = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nSubject: ${formData.subject}\nMessage: ${formData.message}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-swastik-lightEarth/40">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-swastik-green font-medium">Get In Touch</span>
          <h2 className="section-title mx-auto after:left-1/2 after:-translate-x-1/2">Contact Us</h2>
          <p className="section-subtitle">
            Have questions about our treatments or want to schedule a consultation? Reach out to us and our team will get back to you as soon as possible.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 order-2 lg:order-1">
            <h3 className="text-2xl font-serif font-bold text-swastik-blue mb-6">Send Us a Message</h3>
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <Input 
                    id="name" 
                    placeholder="Full Name" 
                    className="w-full" 
                    required 
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="email@example.com" 
                    className="w-full" 
                    required 
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <Input 
                    id="phone" 
                    placeholder="Your Phone Number" 
                    className="w-full" 
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <Input 
                    id="subject" 
                    placeholder="How can we help you?" 
                    className="w-full" 
                    value={formData.subject}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <Textarea 
                  id="message" 
                  placeholder="Please describe your condition or inquiry" 
                  className="w-full min-h-[120px]" 
                  required 
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </div>
              
              <Button type="submit" className="w-full md:w-auto">Send Message via WhatsApp</Button>
            </form>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 mb-8">
              <h3 className="text-2xl font-serif font-bold text-swastik-blue mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-swastik-blue/10 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-swastik-blue" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium text-gray-900">Our Location</h4>
                    <p className="text-gray-600 mt-1">
                      #250/42, 2nd Street, TNHB Colony,<br />
                      (Near Canara Bank)<br />
                      Korattur, Chennai - 600 080
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-swastik-blue/10 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-swastik-blue" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium text-gray-900">Phone Number</h4>
                    <p className="text-gray-600 mt-1">+91-98401 93160, +91-6385044414</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-swastik-blue/10 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-swastik-blue" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium text-gray-900">Email Address</h4>
                    <p className="text-gray-600 mt-1">shrivaraham.sync@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-swastik-blue/10 p-3 rounded-full">
                    <Clock className="h-6 w-6 text-swastik-blue" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium text-gray-900">Working Hours</h4>
                    <p className="text-gray-600 mt-1">Monday - Saturday: 9:00 AM - 8:00 PM</p>
                    <p className="text-gray-600">Sunday: 9:00 AM - 2:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Google Map */}
            <div className="w-full h-80 rounded-lg overflow-hidden shadow-xl">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.0129249999997!2d80.1656!3d13.1067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5265ea4f7d3361%3A0x6e61a70b6863d433!2sKorattur%2C%20Chennai%2C%20Tamil%20Nadu%20600080!5e0!3m2!1sen!2sin!4v1713771677525!5m2!1sen!2sin" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                title="Shri Varaham Swastik Yoga & Naturopathy Clinic Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
